import { Heart, Shield, Award } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'

export default function About() {
  return (
    <div className="py-24 sm:py-32 bg-gray-50 min-h-screen">
      <Helmet>
        <title>About Us | Chelsea Travel</title>
        <meta
          name="description"
          content="Chelsea Travel has nearly 30 years of experience creating bespoke travel itineraries for leisure and corporate clients."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/about' : '/about'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Us | Chelsea Travel" />
        <meta
          property="og:description"
          content="Trusted South African travel consultants crafting premium travel experiences."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/about'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Us',
            url: typeof window !== 'undefined' ? window.location.href : '/about',
            description:
              'Chelsea Travel has nearly 30 years of experience creating bespoke travel itineraries for leisure and corporate clients.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'About',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/about',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Why Choose Chelsea Travel */}
        <div className="mx-auto max-w-3xl text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
            Why Choose <span className="text-[#58b8cd]">Chelsea Travel?</span>
          </h2>
          <p className="text-lg text-gray-700">
            Chelsea Travel (Pty) Ltd has been in business for nearly 30 years. Our senior
            consultants are full-service travel experts, offering personalised, bespoke and premium
            travel itineraries to leisure and corporate travellers. Our access to cutting-edge
            technology enables us to offer our clients up-to-date information, thereby ensuring a
            premium, stress-free experience for your trip, wherever you choose to travel in the
            world.
          </p>
        </div>

        {/* Trusted Travel Partner Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
          <div className="rounded-2xl overflow-hidden shadow-lg h-80 bg-gray-100 flex items-center justify-center">
            <img
              src="/handshake.jpg"
              alt="South African wildlife"
              className="object-cover w-full h-full"
            />
          </div>
          <div>
            <div className="mb-4 w-full text-center">
              <span
                className="font-bold italic text-2xl md:text-3xl whitespace-nowrap"
                style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
              >
                People trust us, because they know that we care!
              </span>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-3xl font-extrabold text-[#58b8cd] mb-1">30+</div>
                <div className="text-sm text-gray-700">Years of Excellence</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-extrabold text-[#58b8cd] mb-1">1000+</div>
                <div className="text-sm text-gray-700">Satisfied Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-extrabold text-[#58b8cd] mb-1">99%</div>
                <div className="text-sm text-gray-700">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-extrabold text-[#58b8cd] mb-1">24/7</div>
                <div className="text-sm text-gray-700">Travel Support</div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-8 mt-16">
          <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
            <Award className="h-10 w-10 text-[#58b8cd] mb-4" />
            <h4 className="font-bold text-lg text-gray-900 mb-2">Award-Winning Excellence</h4>
            <p className="text-gray-700 text-sm">
              Recognized for outstanding service and attention to detail.
            </p>
          </div>
          <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
            <Heart className="h-10 w-10 text-[#58b8cd] mb-4" />
            <h4 className="font-bold text-lg text-gray-900 mb-2">Personalized Service</h4>
            <p className="text-gray-700 text-sm">
              Every itinerary is crafted specifically for your unique travel dreams.
            </p>
          </div>
          <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
            <Shield className="h-10 w-10 text-[#58b8cd] mb-4" />
            <h4 className="font-bold text-lg text-gray-900 mb-2">Trusted & Reliable</h4>
            <p className="text-gray-700 text-sm">
              Decades of experience ensuring your travels are safe and seamless.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
